/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getFeeBeneficiaryDecoder,
  getFeeBeneficiaryEncoder,
  type FeeBeneficiary,
  type FeeBeneficiaryArgs,
} from '../types';

export const LAUNCH_FEE_STATE_DISCRIMINATOR = new Uint8Array([
  1, 106, 55, 241, 179, 18, 230, 232,
]);

export function getLaunchFeeStateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    LAUNCH_FEE_STATE_DISCRIMINATOR,
  );
}

export type LaunchFeeState = {
  discriminator: ReadonlyUint8Array;
  launch: Address;
  beneficiaryLen: number;
  bump: number;
  protocolFeeBps: number;
  swapFeeBps: number;
  version: number;
  pad0: ReadonlyUint8Array;
  beneficiaries: Array<FeeBeneficiary>;
  cumulatedBaseFees: bigint;
  cumulatedQuoteFees: bigint;
  distributedProtocolBaseFees: bigint;
  distributedProtocolQuoteFees: bigint;
  distributedBaseByBeneficiary: Array<bigint>;
  distributedQuoteByBeneficiary: Array<bigint>;
  reserved: ReadonlyUint8Array;
};

export type LaunchFeeStateArgs = {
  launch: Address;
  beneficiaryLen: number;
  bump: number;
  protocolFeeBps: number;
  swapFeeBps: number;
  version: number;
  pad0: ReadonlyUint8Array;
  beneficiaries: Array<FeeBeneficiaryArgs>;
  cumulatedBaseFees: number | bigint;
  cumulatedQuoteFees: number | bigint;
  distributedProtocolBaseFees: number | bigint;
  distributedProtocolQuoteFees: number | bigint;
  distributedBaseByBeneficiary: Array<number | bigint>;
  distributedQuoteByBeneficiary: Array<number | bigint>;
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link LaunchFeeStateArgs} account data. */
export function getLaunchFeeStateEncoder(): FixedSizeEncoder<LaunchFeeStateArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['launch', getAddressEncoder()],
      ['beneficiaryLen', getU8Encoder()],
      ['bump', getU8Encoder()],
      ['protocolFeeBps', getU16Encoder()],
      ['swapFeeBps', getU16Encoder()],
      ['version', getU8Encoder()],
      ['pad0', fixEncoderSize(getBytesEncoder(), 1)],
      [
        'beneficiaries',
        getArrayEncoder(getFeeBeneficiaryEncoder(), { size: 8 }),
      ],
      ['cumulatedBaseFees', getU64Encoder()],
      ['cumulatedQuoteFees', getU64Encoder()],
      ['distributedProtocolBaseFees', getU64Encoder()],
      ['distributedProtocolQuoteFees', getU64Encoder()],
      [
        'distributedBaseByBeneficiary',
        getArrayEncoder(getU64Encoder(), { size: 8 }),
      ],
      [
        'distributedQuoteByBeneficiary',
        getArrayEncoder(getU64Encoder(), { size: 8 }),
      ],
      ['reserved', fixEncoderSize(getBytesEncoder(), 128)],
    ]),
    (value) => ({ ...value, discriminator: LAUNCH_FEE_STATE_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link LaunchFeeState} account data. */
export function getLaunchFeeStateDecoder(): FixedSizeDecoder<LaunchFeeState> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['launch', getAddressDecoder()],
    ['beneficiaryLen', getU8Decoder()],
    ['bump', getU8Decoder()],
    ['protocolFeeBps', getU16Decoder()],
    ['swapFeeBps', getU16Decoder()],
    ['version', getU8Decoder()],
    ['pad0', fixDecoderSize(getBytesDecoder(), 1)],
    ['beneficiaries', getArrayDecoder(getFeeBeneficiaryDecoder(), { size: 8 })],
    ['cumulatedBaseFees', getU64Decoder()],
    ['cumulatedQuoteFees', getU64Decoder()],
    ['distributedProtocolBaseFees', getU64Decoder()],
    ['distributedProtocolQuoteFees', getU64Decoder()],
    [
      'distributedBaseByBeneficiary',
      getArrayDecoder(getU64Decoder(), { size: 8 }),
    ],
    [
      'distributedQuoteByBeneficiary',
      getArrayDecoder(getU64Decoder(), { size: 8 }),
    ],
    ['reserved', fixDecoderSize(getBytesDecoder(), 128)],
  ]);
}

/** Gets the codec for {@link LaunchFeeState} account data. */
export function getLaunchFeeStateCodec(): FixedSizeCodec<
  LaunchFeeStateArgs,
  LaunchFeeState
> {
  return combineCodec(getLaunchFeeStateEncoder(), getLaunchFeeStateDecoder());
}

export function decodeLaunchFeeState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<LaunchFeeState, TAddress>;
export function decodeLaunchFeeState<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<LaunchFeeState, TAddress>;
export function decodeLaunchFeeState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<LaunchFeeState, TAddress> | MaybeAccount<LaunchFeeState, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getLaunchFeeStateDecoder(),
  );
}

export async function fetchLaunchFeeState<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<LaunchFeeState, TAddress>> {
  const maybeAccount = await fetchMaybeLaunchFeeState(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeLaunchFeeState<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<LaunchFeeState, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeLaunchFeeState(maybeAccount);
}

export async function fetchAllLaunchFeeState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<LaunchFeeState>[]> {
  const maybeAccounts = await fetchAllMaybeLaunchFeeState(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeLaunchFeeState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<LaunchFeeState>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeLaunchFeeState(maybeAccount),
  );
}

export function getLaunchFeeStateSize(): number {
  return 656;
}
